
import React, { useMemo } from "react";
import styles from "../hotelRecommendInTheCity/HotelRecommendInTheCity.module.css";

const MAX_RECOMMENDATIONS = 6;

const getValue = (value) => {
  if (!value) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  if (typeof value === "object") {
    return String(
      value.name ?? value.state_name ?? value.title ?? ""
    ).trim();
  }

  return "";
};

const normalize = (value) =>
  getValue(value).toLowerCase().replace(/\s+/g, " ");

const getHotelId = (hotel) =>
  hotel?.id ?? hotel?.pk ?? hotel?._id ?? null;

const getHotelSlug = (hotel) =>
  hotel?.slug ?? hotel?.hotel_slug ?? "";

const getHotelImage = (hotel) =>
  hotel?.cover_image ??
  hotel?.featured_image ??
  hotel?.thumbnail ??
  hotel?.image ??
  hotel?.main_image ??
  "";

const getHotelHref = (hotel) => {
  const slug = getHotelSlug(hotel);
  const rawUrl = hotel?.profile_url || hotel?.url;

  if (rawUrl) {
    if (rawUrl.startsWith("/venue-planner/hotel/")) {
      return rawUrl;
    }

    if (rawUrl.startsWith("/hotel/")) {
      return `/venue-planner${rawUrl}`;
    }
  }

  return slug ? `/venue-planner/hotel/${slug}` : null;
};

const HotelRecommendInTheState = ({
  currentHotel,
  hotels = [],
  title,
  maxItems = MAX_RECOMMENDATIONS,
}) => {
  const currentState = getValue(
    currentHotel?.state ?? currentHotel?.state_name
  );

  const currentId = getHotelId(currentHotel);
  const currentSlug = normalize(getHotelSlug(currentHotel));

  const recommendedHotels = useMemo(() => {
    if (!currentState || !Array.isArray(hotels)) {
      return [];
    }

    const seen = new Set();

    return hotels
      .filter((hotel) => {
        if (!hotel) return false;

        const hotelState = getValue(
          hotel.state ?? hotel.state_name
        );

        if (normalize(hotelState) !== normalize(currentState)) {
          return false;
        }

        const hotelId = getHotelId(hotel);
        const hotelSlug = normalize(getHotelSlug(hotel));

        if (
          currentId != null &&
          hotelId != null &&
          String(currentId) === String(hotelId)
        ) {
          return false;
        }

        if (
          currentSlug &&
          hotelSlug &&
          currentSlug === hotelSlug
        ) {
          return false;
        }

        const uniqueKey =
          hotelId != null
            ? `id:${hotelId}`
            : hotelSlug
              ? `slug:${hotelSlug}`
              : `name:${normalize(
                  hotel.name ?? hotel.hotel_name
                )}`;

        if (seen.has(uniqueKey)) return false;

        seen.add(uniqueKey);
        return true;
      })
      .slice(0, Math.max(0, maxItems));
  }, [hotels, currentState, currentId, currentSlug, maxItems]);

  if (recommendedHotels.length === 0) {
    return null;
  }

  return (
    <section
      className={styles.section}
      aria-labelledby="hotel-recommendations-heading"
    >
      <div className={styles.header}>
        <div className={styles.headingContent}>
       

          <h2
            id="hotel-recommendations-heading"
            className={styles.heading}
          >
            {title || (
              <>
                Explore Other Hotels in{" "}
                <span className={styles.highlightCity}>
                  {currentState}
                </span>
              </>
            )}
          </h2>

          
        </div>
      </div>

      <div className={styles.grid}>
        {recommendedHotels.map((hotel, index) => {
          const name =
            getValue(hotel.name ?? hotel.hotel_name) ||
            "Hotel";

          const city = getValue(
            hotel.city ?? hotel.city_name
          );

          const state = getValue(
            hotel.state ?? hotel.state_name
          );

          const image = getHotelImage(hotel);
          const href = getHotelHref(hotel);

          const key =
            getHotelId(hotel) ??
            getHotelSlug(hotel) ??
            `${normalize(name)}-${index}`;

          const cardContent = (
            <>
              <div className={styles.imageWrapper}>
                {image ? (
                  <img
                    className={styles.image}
                    src={image}
                    alt={`${name} in ${city || state}`}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={styles.imagePlaceholder}
                    aria-label="Hotel image unavailable"
                  >
                    <span>Hotel</span>
                  </div>
                )}
              </div>

              <div className={styles.cardContent}>
                <h3 className={styles.hotelName}>
                  {name}
                </h3>

                <p className={styles.location}>
                  <svg
                    className={styles.locationIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="10"
                      r="2.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                  </svg>

                  <span>
                    {[city, state]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </p>

                <span className={styles.cardLink}>
                  View Hotel Profile
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </>
          );

          return (
            <article className={styles.card} key={key}>
              {href ? (
                <a
                  className={styles.cardLinkWrapper}
                  href={href}
                  aria-label={`View ${name} hotel profile`}
                >
                  {cardContent}
                </a>
              ) : (
                <div className={styles.cardLinkWrapper}>
                  {cardContent}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default HotelRecommendInTheState;
